import { useEffect, useMemo, useRef, useState } from "react";

const MONTHS_DE = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];
const MONTHS_EN = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const WEEKDAYS_DE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const WEEKDAYS_EN = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function parseISODate(iso: string): Date | null {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

interface DatePickerProps {
  name?: string;
  value: string;
  onChange: (iso: string) => void;
  min?: string;
  max?: string;
  disabledDates?: Set<string>;
  bookedDates?: Set<string>;
  turnoverDates?: Set<string>;
  turnoverTooltip?: string;
  isDe?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export default function DatePicker({
  name,
  value,
  onChange,
  min,
  max,
  disabledDates,
  bookedDates,
  turnoverDates,
  turnoverTooltip,
  isDe = true,
  placeholder,
  disabled,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);

  const initial = parseISODate(value) ?? parseISODate(min ?? "") ?? new Date();
  const [view, setView] = useState({ year: initial.getFullYear(), month: initial.getMonth() });

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const months = isDe ? MONTHS_DE : MONTHS_EN;
  const weekdays = isDe ? WEEKDAYS_DE : WEEKDAYS_EN;

  const format = (iso: string) => {
    const d = parseISODate(iso);
    if (!d) return "";
    return d.toLocaleDateString(isDe ? "de-DE" : "en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const minDate = min ? parseISODate(min) : null;
  const maxDate = max ? parseISODate(max) : null;

  const cells = useMemo(() => {
    const firstDay = new Date(view.year, view.month, 1).getDay();
    const offset = (firstDay + 6) % 7;
    const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
    const result: { day: number | null; iso: string | null; disabled: boolean; isBooked: boolean }[] = [];

    for (let i = 0; i < offset; i++) {
      result.push({ day: null, iso: null, disabled: true, isBooked: false });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(view.year, view.month, d);
      const iso = toISODate(date);
      let disabled = false;
      let isBooked = false;
      if (minDate && date < minDate) disabled = true;
      if (maxDate && date > maxDate) disabled = true;
      if (disabledDates && disabledDates.has(iso)) disabled = true;
      if (bookedDates && bookedDates.has(iso)) isBooked = true;
      result.push({ day: d, iso, disabled, isBooked });
    }
    while (result.length % 7 !== 0) {
      result.push({ day: null, iso: null, disabled: true, isBooked: false });
    }
    return result;
  }, [view, minDate, maxDate, disabledDates, bookedDates]);

  const prevMonth = () =>
    setView((v) => {
      const d = new Date(v.year, v.month - 1, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  const nextMonth = () =>
    setView((v) => {
      const d = new Date(v.year, v.month + 1, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });

  const select = (iso: string) => {
    onChange(iso);
    setOpen(false);
  };

  const isSelectedTurnover = value && turnoverDates ? turnoverDates.has(value) : false;

  return (
    <div ref={wrapperRef} className="relative">
      {name && <input type="hidden" name={name} value={value} />}

      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-md border border-background-300 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors ${
          disabled ? "bg-background-100 cursor-not-allowed opacity-70" : "bg-background-50 cursor-pointer"
        }`}
      >
        <span className={value ? "text-foreground-950" : "text-foreground-400"}>
          {value ? format(value) : placeholder || (isDe ? "Datum wählen" : "Select date")}
        </span>
        <i className="ri-calendar-line text-foreground-500"></i>
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 z-50 w-72 bg-background-50 border border-background-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={prevMonth}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 hover:bg-background-200 cursor-pointer text-foreground-800"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            <div className="font-heading text-foreground-950">
              {months[view.month]} {view.year}
            </div>
            <button
              type="button"
              onClick={nextMonth}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 hover:bg-background-200 cursor-pointer text-foreground-800"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1">
            {weekdays.map((w) => (
              <div key={w} className="text-center text-xs font-medium text-foreground-600 py-1">
                {w}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {cells.map((c, i) => {
              if (c.day === null) {
                return <div key={i} className="aspect-square"></div>;
              }
              const isSelected = c.iso === value;
              const isTurnover = c.iso && turnoverDates ? turnoverDates.has(c.iso) : false;
              return (
                <button
                  key={i}
                  type="button"
                  disabled={c.disabled}
                  onClick={() => c.iso && select(c.iso)}
                  title={isTurnover ? turnoverTooltip : undefined}
                  className={`aspect-square rounded-md text-sm font-medium transition-colors relative ${
                    c.isBooked
                      ? c.disabled
                        ? "bg-red-100/50 text-red-700 cursor-not-allowed"
                        : "bg-[linear-gradient(135deg,rgb(187,247,208)_50%,rgb(254,202,202)_50%)] text-foreground-900 cursor-pointer hover:opacity-80"
                      : c.disabled
                      ? "text-foreground-300 cursor-not-allowed"
                      : isSelected
                      ? "bg-primary-600 text-background-50 cursor-pointer"
                      : isTurnover
                      ? "border border-accent-700 text-foreground-900 cursor-pointer hover:bg-accent-50"
                      : "hover:bg-accent-100 text-foreground-800 cursor-pointer"
                  }`}
                >
                  {c.day}
                  {isTurnover && !isSelected && (
                    <span className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-accent-500" />
                  )}
                  {isTurnover && isSelected && (
                    <span className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-background-50" />
                  )}
                </button>
              );
            })}
          </div>

          {isSelectedTurnover && turnoverTooltip && (
            <div className="mt-3 flex items-start gap-2 text-xs text-accent-700 bg-accent-100 border border-accent-200 rounded-md px-3 py-2">
              <i className="ri-information-line mt-0.5"></i>
              <span>{turnoverTooltip}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}